import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  View, Pressable, FlatList, ActivityIndicator, Image, Modal, KeyboardAvoidingView, Platform,
  StyleSheet, Alert, Dimensions, Linking,
} from 'react-native';
import { Text, TextInput } from '../ui/Text';
import { SafeAreaView } from 'react-native-safe-area-context';
import * as ImagePicker from 'expo-image-picker';
import * as DocumentPicker from 'expo-document-picker';
import * as Clipboard from 'expo-clipboard';
import * as FileSystem from 'expo-file-system/legacy';
import * as Sharing from 'expo-sharing';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { useAuth } from '../auth';
import {
  useRoom, useTimeline, useSyncState, useStoreVersion, useIsAgent, sendText, retrySend, sendTyping, loadOlder, markRead, mediaSource, senderName,
  isGate, isGateOpen, isAnsweredHere, decisionOn, answerGate, GATE_LAPSED, loadThreadRoots, uploadMedia, sendAttachment, getReactions, sendReaction, removeReaction, isModelList, modelChoiceFromKey, parseModelList, useThreadRoots, canDelete, deleteMessage, isRealThread, threadUnreadTotal,
  GateChoice, MEvent, Mention,
} from '../matrix';
import { usePrefs } from '../prefs';
import { MessageRow, dayLabel } from '../ui/MessageRow';
import { ActionSheet, SheetAction } from '../ui/ActionSheet';
import { ThreadsStrip } from '../ui/ThreadsStrip';
import { Composer } from '../ui/Composer';
import { confirmDestructive } from '../ui/confirm';
import { KeyboardShift } from '../ui/KeyboardShift';
import { GateBar, DECIDED } from '../ui/GateBar';
import { radius, makeStyles, useTheme } from '../theme';
import type { RootStackParamList } from '../navTypes';

type Props = NativeStackScreenProps<RootStackParamList, 'Chat'>;
const QUICK_REACTIONS = ['👍', '✅', '❤️', '😂', '👀'];

export default function ChatScreen({ route, navigation }: Props) {
  const theme = useTheme(); const s = useStyles();
  const { roomId, title } = route.params;
  const { userId } = useAuth();
  const room = useRoom(roomId);
  const threadRoots = useThreadRoots(roomId);
  const threadCount = threadRoots.length;
  const threadsLatest = threadRoots.reduce((m, t) => Math.max(m, t.threadLatestTs || 0, t.ts), 0);
  const all = useTimeline(roomId);
  const sync = useSyncState();
  const isAgent = useIsAgent();
  const { humanBadge } = usePrefs();
  useStoreVersion(); // reactions live outside the timeline array — re-render when they change
  // Which roots are real threads (see isRealThread): opened on purpose, started by an agent, or replied to by a
  // human. Hermes's auto-threaded single replies stay flat in the main timeline.
  const realRoots = useMemo(() => {
    const s = new Set<string>();
    for (const e of all) {
      if (e.threadRoot && isRealThread(roomId, e.threadRoot)) s.add(e.threadRoot);
      if ((e.thread || (e.threadCount || 0) > 0) && isRealThread(roomId, e.id)) s.add(e.id);
    }
    return s;
  }, [all, isAgent, roomId]);
  const events = useMemo(() => all.filter((e) => !e.threadRoot || !realRoots.has(e.threadRoot)), [all, realRoots]);
  const threadInfo = useMemo(() => {
    const m = new Map<string, { count: number; latestTs?: number; unread?: number }>();
    for (const e of all) {
      if (!e.threadRoot || !realRoots.has(e.threadRoot)) continue;
      const cur = m.get(e.threadRoot) || { count: 0, latestTs: 0 };
      m.set(e.threadRoot, { count: cur.count + 1, latestTs: Math.max(cur.latestTs || 0, e.ts) });
    }
    for (const e of all) {
      if (!e.threadCount || !realRoots.has(e.id)) continue;
      const cur = m.get(e.id);
      if (!cur || cur.count < e.threadCount) m.set(e.id, { count: e.threadCount, latestTs: Math.max(e.threadLatestTs || 0, cur?.latestTs || 0) || undefined });
    }
    for (const e of all) if (e.thread && realRoots.has(e.id) && !m.has(e.id)) m.set(e.id, { count: 0 }); // a fresh conversation, no replies yet
    for (const [id, v] of m) v.unread = room?.threadUnread?.[id] || 0;
    return m;
  }, [all, realRoots, room?.threadUnread]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [hasOlder, setHasOlder] = useState(true);
  const [replyTo, setReplyTo] = useState<MEvent | null>(null);
  const [mentions, setMentions] = useState<Mention[]>([]); // people/agents tagged in the draft
  const [viewer, setViewer] = useState<MEvent | null>(null);
  const [opening, setOpening] = useState<string | null>(null);
  const [showJump, setShowJump] = useState(false); // new messages arrived while scrolled up
  const [farUp, setFarUp] = useState(false);       // more than a screen away from the latest message
  const [sheet, setSheet] = useState<MEvent | null>(null);
  const [attachOpen, setAttachOpen] = useState(false);
  const [viewerErr, setViewerErr] = useState<string | null>(null);
  const listRef = useRef<FlatList<MEvent>>(null);
  const atBottom = useRef(true);
  const listHeight = useRef(0);
  const lastIdRef = useRef<string | undefined>(undefined);
  const typingRef = useRef<{ on: boolean; at: number; timer?: ReturnType<typeof setTimeout> }>({ on: false, at: 0 });

  // Header: tappable room name (→ room info), model/context chip, live status dot.
  const ai = room?.agentInfo;
  const ctxPct = ai?.context_used != null && ai?.context_max ? Math.round((ai.context_used / ai.context_max) * 100) : null;
  useEffect(() => {
    const color = sync === 'live' ? '#3fb950' : sync === 'error' ? '#f85149' : '#d29922';
    const name = room?.name || title;
    navigation.setOptions({
      headerTitle: () => (
        <Pressable onPress={() => navigation.navigate('RoomInfo', { roomId })} hitSlop={8} accessibilityRole="button" accessibilityLabel="Room details">
          <Text style={{ color: theme.ink, fontWeight: '700', fontSize: 17 }} numberOfLines={1}>{name}</Text>
        </Pressable>
      ),
      headerRight: () => (
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginRight: 4 }}>
          {ai?.model ? (
            <View style={s.chip}><Text style={s.chipText} numberOfLines={1}>{String(ai.model)}{ctxPct != null ? ` · ${ctxPct}%` : ''}</Text></View>
          ) : null}
          <View style={{ width: 8, height: 8, borderRadius: 4, backgroundColor: color }} accessibilityLabel={sync === 'live' ? 'Connected' : sync === 'error' ? 'Reconnecting' : 'Syncing'} />
        </View>
      ),
    });
  }, [title, room?.name, sync, ai?.model, ctxPct, roomId]);

  // The room's thread list comes from the server: the loaded messages only show the threads that started
  // inside them, so without this the count appeared late, or only after a visit to the Threads page.
  useEffect(() => { loadThreadRoots(roomId, 60_000).catch(() => {}); }, [roomId]);

  // Mark read whenever new messages land while the room is open.
  useEffect(() => { markRead(roomId); }, [roomId, all.length]);

  // Stop advertising "typing" when leaving the room.
  useEffect(() => () => {
    if (typingRef.current.on) sendTyping(roomId, false);
    if (typingRef.current.timer) clearTimeout(typingRef.current.timer);
  }, [roomId]);

  const stopTyping = () => {
    if (typingRef.current.timer) clearTimeout(typingRef.current.timer);
    if (typingRef.current.on) { sendTyping(roomId, false); typingRef.current.on = false; }
  };
  const onChangeText = (t: string) => {
    setText(t);
    if (!t.trim()) { stopTyping(); return; }
    const now = Date.now();
    if (!typingRef.current.on || now - typingRef.current.at > 15000) {
      sendTyping(roomId, true);
      typingRef.current.on = true; typingRef.current.at = now;
    }
    if (typingRef.current.timer) clearTimeout(typingRef.current.timer);
    typingRef.current.timer = setTimeout(() => { sendTyping(roomId, false); typingRef.current.on = false; }, 6000);
  };

  const older = async () => {
    if (loadingOlder) return;
    setLoadingOlder(true);
    try { setHasOlder(await loadOlder(roomId)); } catch { /* keep button */ } finally { setLoadingOlder(false); }
  };

  // On the web the list's own scrollToEnd guesses the end from item measurements it may not have yet, and lands
  // short by the height of whatever was added last (a message, its Seen line, a reaction). The browser's scroll
  // box knows the real end, so that is what gets scrolled there. react-native-web puts its own scrollTo({x, y})
  // onto that box (the browser's scrollTo({top}) is gone) and a scrollToEnd that reads the box's real
  // scrollHeight: that one is used. Phones keep the list's own call.
  const toEnd = (animated: boolean) => {
    type Box = { scrollToEnd?: (o: { animated: boolean }) => void; scrollTop: number; scrollHeight: number };
    const box = Platform.OS === 'web' ? ((listRef.current as unknown as { getScrollableNode?: () => Box })?.getScrollableNode?.()) : undefined;
    if (box) { if (box.scrollToEnd) box.scrollToEnd({ animated }); else box.scrollTop = box.scrollHeight; return; }
    listRef.current?.scrollToEnd({ animated });
  };
  const scrollToEnd = (animated = true) => { atBottom.current = true; setShowJump(false); setFarUp(false); toEnd(animated); };

  const send = async () => {
    const body = text.trim();
    if (!body || sending || room?.encrypted) return;
    const reply = replyTo; const tagged = mentions;
    setSending(true); setText(''); setReplyTo(null); setMentions([]); stopTyping();
    scrollToEnd(false);
    try { await sendText(roomId, body, { replyTo: reply?.id, mentions: tagged }); }
    catch { /* stays in the thread as "Not sent — tap to retry" */ }
    finally { setSending(false); }
  };

  // ── @mentions: an "@word" at the end of the draft opens a chip row of members + agents. ──
  const mentionQuery = useMemo(() => { const m = /(?:^|\s)@([\w.-]*)$/.exec(text); return m ? m[1].toLowerCase() : null; }, [text]);
  const mentionMatches = useMemo(() => {
    if (mentionQuery === null || !room) return [];
    return Object.entries(room.members)
      .filter(([uid, name]) => uid !== userId && (name.toLowerCase().includes(mentionQuery) || uid.toLowerCase().includes(mentionQuery)))
      .slice(0, 6).map(([id, name]) => ({ id, name }));
  }, [mentionQuery, room, userId]);
  const pickMention = (m: Mention) => {
    setText((t) => t.replace(/(^|\s)@([\w.-]*)$/, (_, lead) => `${lead}@${m.name} `));
    setMentions((cur) => (cur.some((x) => x.id === m.id) ? cur : [...cur, m]));
  };

  const answer = async (ev: MEvent, choice: GateChoice, note?: string) => {
    try { await answerGate(roomId, ev, choice, note); } catch { /* store rolls back */ }
  };

  // ── Message actions ──
  const react = (ev: MEvent, key: string) => {
    // The agent's model list says "react to choose", but Hermes on Matrix ignores those reactions:
    // a tap on a number pill sends the typed command on the user's behalf instead.
    const n = isAgent(ev.sender) && isModelList(ev) ? modelChoiceFromKey(key) : null;
    if (n != null) {
      const pick = parseModelList(ev.body)?.options.find((o) => o.n === n);   // send the model NAME: Hermes takes a bare number literally
      if (pick) sendText(roomId, `/model ${pick.id}`).catch((e) => Alert.alert('Could not switch model', e?.message));
      return;
    }
    const mine = (getReactions(ev.id)[key] || []).includes(userId || '');
    (mine ? removeReaction(roomId, ev.id, key) : sendReaction(roomId, ev.id, key)).catch((e) => Alert.alert('Reaction failed', e?.message));
  };
  const onLongPress = (ev: MEvent) => { if (ev.kind !== 'system' && !ev.pending) setSheet(ev); };
  const openThread = (ev: MEvent) => navigation.navigate('Thread', { roomId, rootId: ev.id });
  const sheetActions = (ev: MEvent): SheetAction[] => {
    const t = threadInfo.get(ev.id);
    const acts: SheetAction[] = [
      { label: 'Reply', hint: 'Quote it in the main conversation', onPress: () => setReplyTo(ev) },
      t ? { label: `Open thread · ${t.count} ${t.count === 1 ? 'reply' : 'replies'}`, onPress: () => openThread(ev) }
        : { label: 'Reply in thread', hint: 'Keep a side conversation out of the main room', onPress: () => openThread(ev) },
    ];
    if (ev.kind === 'text') acts.push({ label: 'Copy text', onPress: () => { Clipboard.setStringAsync(ev.body).catch(() => {}); } });
    if (canDelete(room, ev)) acts.push({ label: 'Delete message', destructive: true, hint: ev.sender === userId ? 'Removes it for everyone' : 'You can remove this as a room admin', onPress: () => confirmDelete(ev) });
    return acts;
  };
  const confirmDelete = (ev: MEvent) => confirmDestructive('Delete this message?', 'It is removed for everyone in the room and cannot be brought back.', 'Delete',
    () => { deleteMessage(roomId, ev.id).catch((e) => Alert.alert('Could not delete', e?.message)); });
  const openFile = async (ev: MEvent) => {
    const src = mediaSource(ev.mxc); if (!src) return;
    setOpening(ev.id);
    try {
      const safe = (ev.body || 'file').replace(/[^\w.\-]+/g, '_');
      const res = await FileSystem.downloadAsync(src.uri, `${FileSystem.cacheDirectory}${safe}`, { headers: src.headers });
      if (await Sharing.isAvailableAsync()) await Sharing.shareAsync(res.uri);
      else Alert.alert('Downloaded', res.uri);
    } catch (e: any) { Alert.alert('Could not open file', e?.message || 'Download failed'); }
    finally { setOpening(null); }
  };

  // ── Attachments ──
  const sendUpload = async (uri: string, name: string, mime: string, w?: number, h?: number) => {
    setUploading(true);
    try { const up = await uploadMedia(uri, name, mime); await sendAttachment(roomId, { ...up, w, h }, uri); scrollToEnd(); }
    catch (e: any) { Alert.alert('Could not send attachment', e?.message || 'Upload failed'); }
    finally { setUploading(false); }
  };
  const pickImage = async (camera: boolean) => {
    // The photo library needs no permission (the system picker runs outside the app); only the camera does.
    if (camera) {
      const perm = await ImagePicker.requestCameraPermissionsAsync();
      if (!perm.granted) {
        Alert.alert('Camera is off for Helm', 'Allow the camera in your phone settings to take a photo here.', [
          { text: 'Not now', style: 'cancel' }, { text: 'Open settings', onPress: () => Linking.openSettings().catch(() => {}) },
        ]);
        return;
      }
    }
    const res = camera
      ? await ImagePicker.launchCameraAsync({ quality: 0.7 })
      : await ImagePicker.launchImageLibraryAsync({ mediaTypes: ['images'], quality: 0.7 });
    if (res.canceled || !res.assets?.length) return;
    const a = res.assets[0];
    await sendUpload(a.uri, a.fileName || a.uri.split('/').pop() || 'photo.jpg', a.mimeType || 'image/jpeg', a.width, a.height);
  };
  const pickFile = async () => {
    const res = await DocumentPicker.getDocumentAsync({ copyToCacheDirectory: true, multiple: false });
    if (res.canceled || !res.assets?.length) return;
    const f = res.assets[0];
    await sendUpload(f.uri, f.name, f.mimeType || 'application/octet-stream');
  };
  // A bottom sheet, not Alert.alert: Android shows at most three Alert buttons, so "Cancel" used to vanish there.
  const attach = () => setAttachOpen(true);
  const attachActions: SheetAction[] = [
    { label: 'Photo library', hint: 'Pick a photo from your phone', onPress: () => pickImage(false).catch((e) => Alert.alert('Could not attach', e?.message)) },
    { label: 'Camera', hint: 'Take a photo now', onPress: () => pickImage(true).catch((e) => Alert.alert('Could not attach', e?.message)) },
    { label: 'File', hint: 'Any document', onPress: () => pickFile().catch((e) => Alert.alert('Could not attach', e?.message)) },
  ];

  // Who's typing (names), and which of my messages has been seen by someone else.
  const typers = (room?.typing || []).map((u) => senderName(room, u));
  // Rows are memoized (MessageRow), so the callbacks they receive must keep their identity across renders while
  // still seeing fresh state: one ref that always holds the latest handlers, and stable wrappers around it.
  const latest = useRef({ answer, react, onLongPress, openThread, openFile });
  latest.current = { answer, react, onLongPress, openThread, openFile };
  const stable = useMemo(() => ({
    answer: (ev: MEvent, c: GateChoice, note?: string) => latest.current.answer(ev, c, note),
    react: (ev: MEvent, key: string) => latest.current.react(ev, key),
    onLongPress: (ev: MEvent) => latest.current.onLongPress(ev),
    openThread: (ev: MEvent) => latest.current.openThread(ev),
    openFile: (ev: MEvent) => latest.current.openFile(ev),
    cardAction: (value: string) => { sendText(roomId, value).catch(() => {}); },
  }), [roomId]);
  const seenId = useMemo(() => {
    if (!room || !userId) return null;
    let myLast = -1;
    for (let i = all.length - 1; i >= 0; i--) if (all[i].sender === userId && !all[i].pending && !all[i].failed && !all[i].threadRoot) { myLast = i; break; }
    if (myLast < 0) return null;
    for (const [u, eid] of Object.entries(room.reads)) {
      if (u === userId) continue;
      const k = all.findIndex((e) => e.id === eid);
      if (k >= myLast) return all[myLast].id;
    }
    return null;
  }, [room, all, userId]);

  const byId = useMemo(() => { const m = new Map<string, MEvent>(); all.forEach((e) => m.set(e.id, e)); return m; }, [all]);
  const viewerSrc = viewer ? mediaSource(viewer.mxc) : null;
  const { width: W, height: H } = Dimensions.get('window');

  return (
    <SafeAreaView style={s.root} edges={['left', 'right', 'bottom']}>
      <KeyboardShift iosBehavior="padding" iosOffset={90}>
        <ThreadsStrip count={threadCount} latestTs={threadsLatest} unread={threadUnreadTotal(room)} onPress={() => navigation.navigate('Threads', { roomId })} />
        <FlatList
          ref={listRef}
          data={events}
          keyExtractor={(e) => e.id}
          contentContainerStyle={{ padding: 12, paddingBottom: 8 }}
          // The list box changes height when the typing line, a reply bar or the keyboard appears under it. When
          // we were at the end, stay at the end; otherwise the last message slides under whatever appeared.
          onLayout={(e) => {
            const h = e.nativeEvent.layout.height;
            if (listHeight.current && h !== listHeight.current && atBottom.current) toEnd(false);
            listHeight.current = h;
          }}
          onScroll={(e) => {
            const { contentOffset, contentSize, layoutMeasurement } = e.nativeEvent;
            const dist = contentSize.height - contentOffset.y - layoutMeasurement.height;
            const b = dist < 80;
            atBottom.current = b; if (b && showJump) setShowJump(false);
            const far = dist > layoutMeasurement.height; if (far !== farUp) setFarUp(far);
          }}
          scrollEventThrottle={100}
          onContentSizeChange={() => {
            // Only a genuinely new last message earns the "↓ New messages" pill — not older history
            // being prepended, and never while we're already at the bottom.
            const lastId = events[events.length - 1]?.id;
            const grewAtEnd = lastId !== lastIdRef.current; lastIdRef.current = lastId;
            if (atBottom.current) { toEnd(false); setShowJump(false); }
            else if (grewAtEnd) setShowJump(true);
          }}
          ListHeaderComponent={
            room?.prevBatch && hasOlder ? (
              <Pressable onPress={older} style={s.older} disabled={loadingOlder}>
                {loadingOlder ? <ActivityIndicator color={theme.accent} size="small" /> : <Text style={s.olderText}>Load earlier messages</Text>}
              </Pressable>
            ) : null
          }
          ListEmptyComponent={
            <View style={s.center}>
              {sync === 'initial' ? <ActivityIndicator color={theme.accent} /> : <Text style={s.empty}>No messages yet — say hi 👋</Text>}
            </View>
          }
          renderItem={({ item, index }) => {
            const prev = events[index - 1];
            const newDay = !prev || new Date(prev.ts).toDateString() !== new Date(item.ts).toDateString();
            const day = newDay ? <Text style={s.day}>{dayLabel(item.ts)}</Text> : null;
            if (item.kind === 'system') return <>{day}<Text style={s.system}>{item.body}</Text></>;
            const cont = !newDay && !!prev && prev.kind !== 'system' && prev.sender === item.sender && item.ts - prev.ts < 5 * 60 * 1000;
            const gate = isGate(item);
            const open = gate && isGateOpen(roomId, item);
            const rx = Object.entries(getReactions(item.id)).filter(([, who]) => who.length);
            const quoted = item.replyTo ? byId.get(item.replyTo) : undefined;
            // A bot quoting the very message it is answering (no human message in between) is noise:
            // the reply obviously belongs to it. Human-made quotes always show.
            let hideQuote = false;
            if (quoted && isAgent(item.sender)) {
              const qi = events.findIndex((e) => e.id === quoted.id);
              hideQuote = qi >= 0 && !events.slice(qi + 1, index).some((e) => e.kind !== 'system' && !isAgent(e.sender));
            }
            const decided = gate && !open ? decisionOn(item) : undefined;
            const gateState = gate ? (open ? 'open' : decided || (isAnsweredHere(item.id) ? 'answered' : 'closed')) : '';
            const footerKey = `${rx.map(([k, who]) => `${k}:${who.length}:${who.includes(userId || '') ? 1 : 0}`).join(',')}|${gateState}|${item.failed ? 'failed' : ''}|${item.id === seenId ? 'seen' : ''}`;
            const footer = (
              <>
                {rx.length > 0 && (
                  <View style={s.rxRow}>
                    {rx.map(([key, who]) => {
                      const mineToo = who.includes(userId || '');
                      return (
                        <Pressable key={key} onPress={() => stable.react(item, key)} style={[s.rx, mineToo && s.rxMine]}>
                          <Text style={s.rxText}>{key} {who.length}</Text>
                        </Pressable>
                      );
                    })}
                  </View>
                )}
                {gate && open && <GateBar always={!item.card} about={item.card?.title} onAnswer={(c, note) => stable.answer(item, c, note)} />}
                {gate && !open && (decided || isAnsweredHere(item.id)) && <Text style={s.answered}>{decided ? DECIDED[decided] : '✓ Answered'}</Text>}
                {gate && !open && !item.card && !decided && !isAnsweredHere(item.id) && <Text style={s.lapsed}>{GATE_LAPSED}</Text>}
                {item.failed && (
                  <Pressable onPress={() => retrySend(roomId, item.id).catch(() => {})} hitSlop={6}>
                    <Text style={s.failed}>Not sent — tap to retry</Text>
                  </Pressable>
                )}
                {item.id === seenId && <Text style={s.seen}>Seen</Text>}
              </>
            );
            return (
              <>
                {day}
                <MessageRow
                  item={item} room={room} userId={userId} cont={cont} isAgent={isAgent} humanBadge={humanBadge}
                  quoted={quoted} hideQuote={hideQuote}
                  onLongPress={stable.onLongPress} onOpenImage={setViewer} onOpenFile={stable.openFile} opening={opening}
                  thread={threadInfo.get(item.id) || null} onOpenThread={stable.openThread}
                  onCardAction={stable.cardAction}
                  footer={footer} footerKey={footerKey}
                />
              </>
            );
          }}
        />

        {(showJump || farUp) && (
          <Pressable onPress={() => scrollToEnd()} style={s.jump} accessibilityRole="button" accessibilityLabel="Jump to latest message">
            <Text style={s.jumpText}>{showJump ? '↓ New messages' : '↓ Latest'}</Text>
          </Pressable>
        )}

        {typers.length > 0 && (
          <View style={s.typingRow}>
            <Text style={s.typingText}>
              {typers.length === 1 ? `${typers[0]} is typing…` : `${typers.slice(0, 2).join(' and ')}${typers.length > 2 ? ' and others' : ''} are typing…`}
            </Text>
          </View>
        )}

        {mentionMatches.length > 0 && (
          <View style={s.mentionRow}>
            {mentionMatches.map((m) => (
              <Pressable key={m.id} onPress={() => pickMention(m)} style={({ pressed }) => [s.mentionChip, pressed && { opacity: 0.6 }]}>
                <Text style={s.mentionText} numberOfLines={1}>@{m.name}{isAgent(m.id) ? ' · bot' : ''}</Text>
              </Pressable>
            ))}
          </View>
        )}

        {replyTo && (
          <View style={s.replyBar}>
            <View style={{ flex: 1, minWidth: 0 }}>
              <Text style={s.replyWho} numberOfLines={1}>Replying to {replyTo.sender === userId ? 'yourself' : senderName(room, replyTo.sender)}</Text>
              <Text style={s.replyText} numberOfLines={1}>{replyTo.kind === 'image' ? '📷 Photo' : replyTo.kind === 'file' ? `📎 ${replyTo.body}` : replyTo.body}</Text>
            </View>
            <Pressable onPress={() => setReplyTo(null)} hitSlop={10} accessibilityLabel="Cancel reply"><Text style={s.replyX}>✕</Text></Pressable>
          </View>
        )}

        {room?.encrypted ? (
          <View style={s.notice}><Text style={s.noticeText}>🔒 This room is encrypted — sending isn't supported yet.</Text></View>
        ) : (
          <Composer value={text} onChangeText={onChangeText} onSend={send} placeholder={`Message ${room?.name || title}`}
            onAttach={attach} attaching={uploading} sending={sending} />
        )}
      </KeyboardShift>

      <ActionSheet
        visible={!!sheet}
        title={sheet ? (sheet.sender === userId ? 'You' : senderName(room, sheet.sender)) : undefined}
        subtitle={sheet ? (sheet.kind === 'image' ? '📷 Photo' : sheet.kind === 'file' ? `📎 ${sheet.body}` : sheet.body.replace(/\s+/g, ' ').slice(0, 120)) : undefined}
        reactions={QUICK_REACTIONS} onReact={(k) => { if (sheet) react(sheet, k); }}
        actions={sheet ? sheetActions(sheet) : []} onClose={() => setSheet(null)}
      />

      <ActionSheet visible={attachOpen} title="Attach" actions={attachActions} onClose={() => setAttachOpen(false)} />

      {/* Full-screen image viewer */}
      <Modal visible={!!viewer} transparent animationType="fade" onRequestClose={() => { setViewer(null); setViewerErr(null); }}>
        <Pressable style={s.viewer} onPress={() => { setViewer(null); setViewerErr(null); }}>
          {viewerSrc && !viewerErr && <Image source={viewer?.localUri ? { uri: viewer.localUri } : viewerSrc} style={{ width: W, height: H * 0.8 }} resizeMode="contain" onError={(e) => setViewerErr(e.nativeEvent?.error || 'load failed')} />}
          {!!viewerErr && <Text style={s.viewerHint}>Photo couldn't be loaded from the server ({viewerErr})</Text>}
          <Text style={s.viewerHint}>{viewer?.body}   ·   tap to close</Text>
        </Pressable>
      </Modal>
    </SafeAreaView>
  );
}

const useStyles = makeStyles((theme) => StyleSheet.create({
  root: { flex: 1, backgroundColor: theme.bg },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 24 },
  empty: { color: theme.faint, fontSize: 14 },
  older: { alignItems: 'center', paddingVertical: 10 },
  olderText: { color: theme.accent, fontSize: 13, fontWeight: '600' },
  chip: { backgroundColor: theme.surface2, borderRadius: radius.pill, paddingHorizontal: 8, paddingVertical: 3, maxWidth: 150, borderWidth: 1, borderColor: theme.border },
  chipText: { color: theme.muted, fontSize: 11, fontWeight: '600', fontFamily: theme.mono },
  day: { color: theme.faint, fontSize: 11, fontWeight: '700', letterSpacing: 0.6, textTransform: 'uppercase', textAlign: 'center', marginTop: 16, marginBottom: 2, fontFamily: theme.mono },
  rxRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 6, marginTop: 6 },
  rx: { backgroundColor: theme.surface2, borderRadius: radius.pill, paddingHorizontal: 8, paddingVertical: 3, borderWidth: 1, borderColor: theme.border },
  rxMine: { borderColor: theme.accent, backgroundColor: theme.accentBg },
  rxText: { color: theme.ink, fontSize: 12 },
  seen: { color: theme.faint, fontSize: 11, marginTop: 3 },
  failed: { color: theme.danger, fontSize: 12, fontWeight: '600', marginTop: 4 },
  system: { color: theme.faint, fontSize: 12, textAlign: 'center', marginTop: 10, fontStyle: 'italic' },
  answered: { color: theme.good, fontSize: 12, fontWeight: '600', marginTop: 6 },
  lapsed: { color: theme.faint, fontSize: 12.5, marginTop: 6, fontStyle: 'italic' },
  // jump / typing / reply
  jump: { position: 'absolute', alignSelf: 'center', bottom: 90, backgroundColor: theme.accent, borderRadius: radius.pill, paddingHorizontal: 14, paddingVertical: 8 },
  jumpText: { color: theme.accentInk, fontWeight: '800', fontSize: 13 },
  typingRow: { paddingHorizontal: 16, paddingVertical: 6, backgroundColor: theme.bg },
  typingText: { color: theme.faint, fontSize: 12, fontStyle: 'italic' },
  mentionRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 6, paddingHorizontal: 12, paddingVertical: 8, backgroundColor: theme.surface, borderTopWidth: 1, borderTopColor: theme.border },
  mentionChip: { backgroundColor: theme.surface2, borderRadius: radius.pill, paddingHorizontal: 12, paddingVertical: 7, borderWidth: 1, borderColor: theme.border, maxWidth: 200 },
  mentionText: { color: theme.accent, fontWeight: '700', fontSize: 13.5 },
  replyBar: { flexDirection: 'row', alignItems: 'center', gap: 10, paddingHorizontal: 14, paddingVertical: 8, backgroundColor: theme.surface, borderTopWidth: 1, borderTopColor: theme.border, borderLeftWidth: 3, borderLeftColor: theme.accent },
  replyWho: { color: theme.accent, fontSize: 12, fontWeight: '700' },
  replyText: { color: theme.muted, fontSize: 13 },
  replyX: { color: theme.faint, fontSize: 18, paddingHorizontal: 6 },
  notice: { padding: 12, borderTopWidth: 1, borderTopColor: theme.border, backgroundColor: theme.surface, alignItems: 'center' },
  noticeText: { color: theme.muted, fontSize: 13 },
  // image viewer
  viewer: { flex: 1, backgroundColor: 'rgba(0,0,0,0.96)', alignItems: 'center', justifyContent: 'center' },
  viewerHint: { color: '#aaa', fontSize: 12, marginTop: 12 },
}));
